import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { roomEntityHandleForSelectingReportingUnit } from "building-types/handles/roomEntityHandleForSelectingReportingUnit";
import type { Room } from "building-types/rooms/roomTypes";
import type { Table } from "common/data/table";
import { aabb2ContainsVec2, aabb2SignedDistanceTo } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { classifyContainmentVec2PolyLine2Fast } from "geom/containment";
import { polyLine2AABB2 } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";

export interface FloorRoomRegion {
  readonly outline: PolyLine2;
  readonly bb: AABB2;
  readonly reportingUnitHandle: DocEntityHandle | undefined;
  /** The room's hosting component instance, when it has one. */
  readonly componentInstanceHandle: DocEntityHandle | undefined;
}

/**
 * Room outlines on a level, each paired with the handles its room resolves to. Floor assemblies are
 * grouped by construction parameters, not by unit or by component instance, so one assembly can span
 * several of either; these regions let each piece of floor geometry (a framing member or a
 * floor/slab surface) be attributed to the room it sits in.
 */
export function floorRoomRegions(
  levelId: string,
  rooms: Table<Room>,
  roomsByLevel: Table<string[]>,
): FloorRoomRegion[] {
  const regions: FloorRoomRegion[] = [];
  for (const roomId of roomsByLevel[levelId] ?? []) {
    const room = rooms[roomId];
    const outline = room?.outline;
    if (!outline || outline.length < 6) {
      continue;
    }
    regions.push({
      outline,
      bb: polyLine2AABB2(outline),
      reportingUnitHandle: roomEntityHandleForSelectingReportingUnit(room),
      componentInstanceHandle: room.instanceId ? { type: "COMPONENT_INSTANCE", id: room.instanceId } : undefined,
    });
  }
  return regions;
}

/**
 * The room a piece of floor geometry sits in, found by a representative XY point: the room whose
 * outline contains the point, or — for pieces on a perimeter edge, whose point lands on the outline
 * — the nearest room. Undefined only when the level has no rooms to attribute against.
 */
export function floorRegionForPoint(
  x: number,
  y: number,
  regions: readonly FloorRoomRegion[],
): FloorRoomRegion | undefined {
  if (regions.length === 0) {
    return undefined;
  }

  let nearest = regions[0];
  let nearestDist = Infinity;
  for (const region of regions) {
    if (aabb2ContainsVec2(region.bb, x, y) && classifyContainmentVec2PolyLine2Fast(x, y, region.outline)) {
      return region;
    }
    const dist = aabb2SignedDistanceTo(region.bb, x, y);
    if (dist < nearestDist) {
      nearestDist = dist;
      nearest = region;
    }
  }
  return nearest;
}
